import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

/**
 * FriendsOnlineWidget - Discord-style social presence indicator
 *
 * Shows friends currently studying - drives FOMO and engagement
 * Per UX guide: "Teens check who's online constantly"
 *
 * Design Philosophy (Snapchat Model - December 2025):
 * - Session codes are the PRIMARY connection mechanism
 * - "Join with Code" is the main action - instant, no approval gate
 * - Study buddy connections emerge AFTER studying together (post-session prompt)
 * - No "Add Friend" request/approval flow (Facebook-era, deprecated)
 * - Connection is a REWARD for studying together, not a GATE
 *
 * Cash App Principle: "Remove uncertainty"
 * - Every action has an immediate, predictable result
 * - Enter code → You're studying together. That's it.
 */

// SVG Icons
const Icons = {
  Users: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  ChevronDown: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 9 6 6 6-6"/>
    </svg>
  ),
  Phone: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  ),
  // FaceTime-style invite icon
  Send: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m22 2-7 20-4-9-9-4Z"/>
      <path d="M22 2 11 13"/>
    </svg>
  ),
  // Add friend icon
  UserPlus: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <line x1="19" y1="8" x2="19" y2="14"/>
      <line x1="22" y1="11" x2="16" y2="11"/>
    </svg>
  ),
};

const FriendsOnlineWidget = ({
  user,
  onJoinFriend,
  onInviteFriend,  // Legacy: Enter friend code
  onAddFriend,     // New: Open @username search modal
  onDirectInvite,  // New: FaceTime-style invite to session
  onFriendsUpdate,
  isInSession = false  // Whether user is currently in a voice session
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('friends');
  const [onlineFriends, setOnlineFriends] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(false);
  const [invitingFriend, setInvitingFriend] = useState(null); // Track which friend is being invited
  // Note: Friend codes deprecated in favor of session-first model (Dec 2025)
  // Connections now emerge from studying together, not upfront friend requests

  // Real-time presence via SSE (Server-Sent Events) - FERPA compliant
  // Replaces polling with push-based updates from Redis pub/sub
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const friendsMapRef = useRef(new Map()); // Track friend presence by user_id
  const lastFetchRef = useRef(0); // Debounce fetches
  const isFetchingRef = useRef(false);
  const isConnectingRef = useRef(false); // Prevent duplicate SSE connections
  const retryCountRef = useRef(0); // Track consecutive SSE failures for auth detection
  const MAX_SSE_RETRIES = 5;
  const SSE_BASE_DELAY = 5000; // 5 seconds

  // FERPA: Clear all student data from memory
  const clearStudentData = () => {
    friendsMapRef.current.clear();
    setOnlineFriends([]);
    setActivityFeed([]);
  };

  // Listen for auth:logout from anywhere in the app (FERPA: ensure data cleanup)
  useEffect(() => {
    const handleAuthLogout = () => {
      // Close SSE connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      isConnectingRef.current = false;
      retryCountRef.current = 0;
      // FERPA: Clear all student presence data from memory
      clearStudentData();
    };

    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Initial fetch to get full friend data (username, streak, etc.)
    const fetchFullFriendData = async () => {
      // Debounce: skip if fetched within last 2 seconds
      const now = Date.now();
      if (isFetchingRef.current || now - lastFetchRef.current < 2000) {
        return;
      }
      isFetchingRef.current = true;

      try {
        const response = await axios.get('/api/voice/friends/online');
        const friends = response.data.friends || [];
        lastFetchRef.current = Date.now();

        // Build map for quick lookup
        friends.forEach(f => {
          friendsMapRef.current.set(f.friend_user_id || f.id, f);
        });

        setOnlineFriends(friends);
        setLoading(false);

        if (onFriendsUpdate) {
          onFriendsUpdate(friends);
        }
      } catch (error) {
        console.error('🎯 SSE: Error fetching initial friends:', error);
        setLoading(false);
      } finally {
        isFetchingRef.current = false;
      }
    };

    // Connect to SSE stream for real-time presence updates
    const connectSSE = () => {
      // Prevent duplicate connections (React StrictMode)
      if (isConnectingRef.current) {
        return;
      }
      // Don't reconnect if already connected
      if (eventSourceRef.current && eventSourceRef.current.readyState !== EventSource.CLOSED) {
        return;
      }

      isConnectingRef.current = true;
      const eventSource = new EventSource('/api/voice/presence/stream', {
        withCredentials: true
      });

      eventSource.onopen = () => {
        isConnectingRef.current = false;
        retryCountRef.current = 0; // Reset on successful connection
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle reconnect request from server (FERPA: re-auth after timeout)
          if (data.type === 'reconnect') {
            eventSource.close();
            eventSourceRef.current = null;
            isConnectingRef.current = false;
            // Reconnect after brief delay
            reconnectTimeoutRef.current = setTimeout(connectSSE, 1000);
            return;
          }

          if (data.type === 'init') {
            // Initial state from server - update online status for each friend
            const presenceMap = new Map();
            data.friends.forEach(f => {
              presenceMap.set(f.user_id, f);
            });

            // Update our friends list with presence data
            setOnlineFriends(prev => {
              // If we have full friend data, merge presence
              if (friendsMapRef.current.size > 0) {
                const updated = [];
                friendsMapRef.current.forEach((friend, friendId) => {
                  const presence = presenceMap.get(friendId);
                  if (presence && presence.online) {
                    updated.push({
                      ...friend,
                      subject: presence.subject || friend.subject,
                      can_join: presence.can_join
                    });
                  }
                });
                return updated;
              }
              return prev;
            });
          } else if (data.type === 'update') {
            // Real-time presence update
            const presence = data.presence;

            setOnlineFriends(prev => {
              const friendData = friendsMapRef.current.get(presence.user_id);

              if (presence.online) {
                // Friend came online - add or update
                const existing = prev.find(f => (f.friend_user_id || f.id) === presence.user_id);
                if (existing) {
                  // Update existing
                  return prev.map(f =>
                    (f.friend_user_id || f.id) === presence.user_id
                      ? { ...f, subject: presence.subject, can_join: presence.can_join }
                      : f
                  );
                } else if (friendData) {
                  // Add new online friend
                  return [...prev, {
                    ...friendData,
                    subject: presence.subject,
                    can_join: presence.can_join
                  }];
                }
              } else {
                // Friend went offline - remove from list
                return prev.filter(f => (f.friend_user_id || f.id) !== presence.user_id);
              }
              return prev;
            });
          }
        } catch (error) {
          console.error('🎯 SSE: Error parsing event:', error);
        }
      };

      eventSource.onerror = (error) => {
        eventSource.close();
        eventSourceRef.current = null;
        isConnectingRef.current = false;
        retryCountRef.current += 1;

        // Hard stop after max retries — likely a persistent auth failure
        if (retryCountRef.current > MAX_SSE_RETRIES) {
          console.warn('🎯 SSE: Max retries exceeded, triggering logout');
          clearStudentData();
          window.dispatchEvent(new CustomEvent('auth:logout'));
          return;
        }

        // Auth probe: check if token is still valid before reconnecting
        // EventSource API doesn't expose HTTP status codes, so we use a lightweight
        // fetch to distinguish auth failure from transient network errors
        fetch('/api/voice/friends/online', { credentials: 'include' })
          .then(response => {
            if (response.status === 401) {
              // Auth is expired — FERPA: clear student data and force re-login
              console.warn('🎯 SSE: Auth expired, clearing student data and redirecting to login');
              clearStudentData();
              window.dispatchEvent(new CustomEvent('auth:logout'));
            } else {
              // Auth is valid — transient error, reconnect with exponential backoff
              const delay = Math.min(SSE_BASE_DELAY * Math.pow(2, retryCountRef.current - 1), 60000);
              console.log(`🎯 SSE: Transient error, reconnecting in ${delay / 1000}s (attempt ${retryCountRef.current}/${MAX_SSE_RETRIES})`);
              reconnectTimeoutRef.current = setTimeout(connectSSE, delay);
            }
          })
          .catch(() => {
            // Network error on probe — treat as transient, reconnect with backoff
            const delay = Math.min(SSE_BASE_DELAY * Math.pow(2, retryCountRef.current - 1), 60000);
            console.log(`🎯 SSE: Network error, reconnecting in ${delay / 1000}s (attempt ${retryCountRef.current}/${MAX_SSE_RETRIES})`);
            reconnectTimeoutRef.current = setTimeout(connectSSE, delay);
          });
      };

      eventSourceRef.current = eventSource;
    };

    // Fetch full data first, then connect SSE
    fetchFullFriendData().then(() => {
      connectSSE();
    });

    // Fallback: Refresh full friend data every 2 minutes (for streaks, etc.)
    const fallbackInterval = setInterval(fetchFullFriendData, 120000);

    return () => {
      // Clean up on unmount
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      isConnectingRef.current = false;
      clearInterval(fallbackInterval);
      // FERPA: Clear student presence data from memory on unmount
      friendsMapRef.current.clear();
    };
    // Note: onFriendsUpdate intentionally excluded to prevent reconnection loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fetch activity feed when Activity tab is selected
  useEffect(() => {
    const fetchActivity = async () => {
      if (!user || activeTab !== 'activity') return;

      setActivityLoading(true);
      try {
        const response = await axios.get('/api/voice/friends/activity');
        setActivityFeed(response.data.activity || []);
      } catch (error) {
        console.error('Error fetching friend activity:', error);
        setActivityFeed([]);
      } finally {
        setActivityLoading(false);
      }
    };

    fetchActivity();
  }, [user, activeTab]);

  // Helper to format relative time
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return 'yesterday';
  };

  // FaceTime-style direct invite handler
  const handleDirectInvite = async (friend) => {
    if (!isInSession) {
      // Can't invite if not in a session
      return;
    }

    setInvitingFriend(friend.id);

    try {
      const response = await axios.post('/api/friends/invite-to-session', {
        friend_user_id: friend.friend_user_id || friend.id,
        subject: 'studying together'
      });

      if (response.data.success) {
        // Show brief success feedback
        setTimeout(() => setInvitingFriend(null), 2000);
        if (onDirectInvite) {
          onDirectInvite(friend, response.data);
        }
      } else {
        setInvitingFriend(null);
        console.error('Invite failed:', response.data.error);
      }
    } catch (error) {
      setInvitingFriend(null);
      console.error('Error sending invite:', error);
    }
  };

  // Show teaser for guests to drive FOMO and account creation
  if (!user) {
    return (
      <div className="friends-widget-container" style={{ position: 'relative' }}>
        <motion.div
          className="friends-online-widget-teaser"
          whileHover={{ scale: 1.02 }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 14px',
            background: 'linear-gradient(135deg, rgba(41, 122, 146, 0.1), rgba(16, 185, 129, 0.1))',
            border: '1px solid rgba(41, 122, 146, 0.2)',
            borderRadius: '9999px',
            cursor: 'default',
          }}
        >
          {/* Pulse dot - shows activity */}
          <span style={{
            width: '8px',
            height: '8px',
            background: '#10b981',
            borderRadius: '50%',
            animation: 'pulse 2s ease-in-out infinite',
          }} />

          {/* Icon */}
          <span style={{ color: '#297A92', display: 'flex' }}>
            <Icons.Users />
          </span>

          {/* Teaser text */}
          <span style={{
            fontSize: '13px',
            fontWeight: 500,
            color: '#297A92',
          }}>
            Friends studying
          </span>

          {/* Lock icon hint */}
          <span style={{
            fontSize: '11px',
            color: '#6b7280',
            marginLeft: '2px'
          }}>
            🔒
          </span>
        </motion.div>

        {/* Tooltip on hover */}
        <style>{`
          .friends-widget-container:hover .friends-teaser-tooltip {
            opacity: 1;
            transform: translateY(0);
          }
        `}</style>
        <div
          className="friends-teaser-tooltip"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '8px',
            padding: '12px 16px',
            background: '#1c1c1e',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            opacity: 0,
            transform: 'translateY(-4px)',
            transition: 'all 0.2s ease',
            zIndex: 100,
            width: '200px',
            pointerEvents: 'none'
          }}
        >
          <p style={{
            margin: 0,
            fontSize: '13px',
            color: '#ffffff',
            lineHeight: 1.4
          }}>
            Create a free account to see friends studying and join their sessions!
          </p>
        </div>
      </div>
    );
  }

  const friendCount = onlineFriends.length;

  return (
    <div className="friends-widget-container" style={{ position: 'relative' }}>
      {/* Main pill button */}
      <motion.button
        className="friends-online-widget"
        onClick={() => setIsExpanded(!isExpanded)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          background: 'white',
          border: 'none',
          borderRadius: '9999px',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          boxShadow: friendCount > 0 ? '0 2px 8px rgba(41, 122, 146, 0.1)' : 'none',
        }}
      >
        {/* Pulse dot when friends online */}
        {friendCount > 0 && (
          <span style={{
            width: '8px',
            height: '8px',
            background: '#10b981',
            borderRadius: '50%',
            animation: 'pulse 2s ease-in-out infinite',
          }} />
        )}

        {/* Icon */}
        <span style={{ color: '#297A92', display: 'flex' }}>
          <Icons.Users />
        </span>

        {/* Text */}
        <span style={{
          fontSize: '14px',
          fontWeight: 500,
          color: '#3D4954',
        }}>
          {loading ? '...' : friendCount > 0 ? `${friendCount} online` : 'Friends'}
        </span>

        {/* Dropdown arrow */}
        <motion.span
          animate={{ rotate: isExpanded ? 180 : 0 }}
          style={{ color: '#8D7F88', display: 'flex' }}
        >
          <Icons.ChevronDown />
        </motion.span>
      </motion.button>

      {/* Dropdown panel */}
      <AnimatePresence>
        {isExpanded && (
          <>
            {/* Invisible backdrop to close dropdown on outside click */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsExpanded(false)}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 999,
                background: 'transparent',
              }}
            />
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="friends-dropdown-panel"
              style={{
                position: 'fixed',
                top: '60px',
                right: '8px',
                left: '8px',
                width: 'auto',
                maxWidth: '320px',
                marginLeft: 'auto',
                maxHeight: 'calc(100vh - 120px)',
                maxHeight: 'calc(100dvh - 120px)',
                background: 'white',
                borderRadius: '16px',
                border: 'none',
                boxShadow: '0 8px 32px rgba(61, 73, 84, 0.15)',
                overflowY: 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                zIndex: 1000,
              }}
            >
            {/* Tab Switcher */}
            <div style={{
              display: 'flex',
              padding: '12px 12px 0',
              gap: '4px',
              borderBottom: '1px solid #ECF3F2',
            }}>
              {[
                { id: 'friends', label: 'Friends', icon: '👥' },
                { id: 'activity', label: 'Activity', icon: '📣' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    background: activeTab === tab.id ? 'white' : 'transparent',
                    border: 'none',
                    borderBottom: activeTab === tab.id ? '2px solid #297A92' : '2px solid transparent',
                    borderRadius: '8px 8px 0 0',
                    color: activeTab === tab.id ? '#297A92' : '#76909F',
                    fontSize: '13px',
                    fontWeight: activeTab === tab.id ? 600 : 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  <span style={{ fontSize: '14px' }}>{tab.icon}</span>
                  {tab.label}
                  {tab.id === 'friends' && friendCount > 0 && (
                    <span style={{
                      background: '#10b981',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 600,
                      padding: '2px 6px',
                      borderRadius: '10px',
                      minWidth: '18px',
                      textAlign: 'center',
                    }}>
                      {friendCount}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Friends Tab Content */}
            {activeTab === 'friends' && (
              <>
                {friendCount > 0 ? (
                  <div style={{
                    maxHeight: '240px',
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                  }}>
                    {onlineFriends.map((friend) => (
                      <div
                        key={friend.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          borderBottom: '1px solid #ECF3F2',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {/* Avatar - glows green when in Mira session (can_join) */}
                          <div style={{
                            position: 'relative',
                            width: '36px',
                            height: '36px',
                          }}>
                            {/* Green glow ring - only when in Mira session */}
                            {friend.can_join && (
                              <div style={{
                                position: 'absolute',
                                inset: '-3px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #10b981, #34d399)',
                                animation: 'pulse-glow 2s ease-in-out infinite',
                              }} />
                            )}
                            <div style={{
                              position: 'relative',
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #297A92, #6DB4CD)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '14px',
                              fontWeight: 600,
                              border: friend.can_join ? '2px solid white' : 'none',
                            }}>
                              {friend.username?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                          </div>

                          <div>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}>
                              <span style={{
                                fontSize: '14px',
                                fontWeight: 500,
                                color: '#3D4954',
                              }}>
                                {friend.username}
                              </span>
                              {/* Friend Streak Badge - Snapchat style */}
                              {friend.streak_days > 0 && (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '2px',
                                    padding: '2px 6px',
                                    background: friend.streak_days >= 7 ? 'linear-gradient(135deg, #f97316, #ef4444)' : '#FEF3C7',
                                    borderRadius: '10px',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    color: friend.streak_days >= 7 ? 'white' : '#D97706',
                                  }}
                                  title={`${friend.streak_days} day streak together!`}
                                >
                                  <span>🔥</span>
                                  <span>{friend.streak_days}</span>
                                </span>
                              )}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#76909F',
                            }}>
                              {friend.subject || 'Studying'}
                            </div>
                          </div>
                        </div>

                        {/* Action buttons - FaceTime style */}
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {/* Join button - if friend is in a session we can join */}
                          {friend.can_join && (
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setIsExpanded(false);
                                onJoinFriend?.(friend);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '6px 12px',
                                background: '#297A92',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: 500,
                                cursor: 'pointer',
                              }}
                            >
                              <Icons.Phone />
                              Join
                            </motion.button>
                          )}

                          {/* Invite button - if I'm in a session, invite them to join me */}
                          {isInSession && !friend.can_join && (
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleDirectInvite(friend)}
                              disabled={invitingFriend === friend.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '6px 12px',
                                background: invitingFriend === friend.id ? '#10b981' : '#ECF3F2',
                                border: 'none',
                                borderRadius: '8px',
                                color: invitingFriend === friend.id ? 'white' : '#297A92',
                                fontSize: '12px',
                                fontWeight: 500,
                                cursor: invitingFriend === friend.id ? 'default' : 'pointer',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              {invitingFriend === friend.id ? (
                                <>✓ Sent</>
                              ) : (
                                <>
                                  <Icons.Send />
                                  Invite
                                </>
                              )}
                            </motion.button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '24px 16px',
                    textAlign: 'center',
                    color: '#76909F',
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>😴</div>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#3D4954' }}>
                      No friends online
                    </div>
                    <div style={{ fontSize: '12px', marginTop: '4px' }}>
                      Invite someone to study together!
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Activity Tab Content */}
            {activeTab === 'activity' && (
              <div style={{
                maxHeight: '280px',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
              }}>
                {activityLoading ? (
                  <div style={{
                    padding: '24px 16px',
                    textAlign: 'center',
                    color: '#76909F',
                    fontSize: '13px',
                  }}>
                    Loading activity...
                  </div>
                ) : activityFeed.length > 0 ? (
                  activityFeed.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        padding: '12px 16px',
                        borderBottom: '1px solid #ECF3F2',
                        gap: '12px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1 }}>
                        {/* Activity icon */}
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: item.type === 'studying' ? '#E0F2FE' :
                                      item.type === 'achievement' ? '#FEF3C7' :
                                      item.type === 'streak' ? '#FFEDD5' : '#F1F5F9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '16px',
                          flexShrink: 0,
                        }}>
                          {item.icon}
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: '13px',
                            color: '#3D4954',
                            lineHeight: 1.4,
                          }}>
                            {item.text}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: '#94A3B8',
                            marginTop: '2px',
                          }}>
                            {formatTimeAgo(item.timestamp)}
                          </div>
                        </div>
                      </div>

                      {/* Join button for studying activities */}
                      {item.can_join && (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setIsExpanded(false);
                            onJoinFriend?.({
                              id: item.friend_id,
                              username: item.friend_name,
                              session_token: item.session_token,
                            });
                          }}
                          style={{
                            padding: '5px 10px',
                            background: '#297A92',
                            border: 'none',
                            borderRadius: '6px',
                            color: 'white',
                            fontSize: '11px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            flexShrink: 0,
                          }}
                        >
                          Join
                        </motion.button>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{
                    padding: '24px 16px',
                    textAlign: 'center',
                    color: '#76909F',
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#3D4954' }}>
                      No recent activity
                    </div>
                    <div style={{ fontSize: '12px', marginTop: '4px' }}>
                      Activity from your friends will show here
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Footer actions - Snapchat model: Session-first connection */}
            <div style={{
              padding: '12px 16px',
              background: '#F5F9FA',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}>
              {/* Primary: Join with Code - THE main action (Snapchat model) */}
              <button
                onClick={() => {
                  setIsExpanded(false);
                  onInviteFriend?.(); // Opens JoinFriendModal for session code entry
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#297A92',
                  border: 'none',
                  borderRadius: '10px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#1E6577';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#297A92';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
                Join with Code
              </button>

              {/* Helper text - Cash App principle: Remove uncertainty */}
              <p style={{
                fontSize: '11px',
                color: '#94A3B8',
                textAlign: 'center',
                margin: 0,
              }}>
                Enter a friend's session code to study together instantly
              </p>
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Pulse animation + Mobile responsive dropdown */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.1); }
        }

        @keyframes pulse-glow {
          0%, 100% { opacity: 0.8; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }

        /* Mobile-first dropdown - 2025 UX */
        @media (max-width: 480px) {
          .friends-dropdown-panel {
            left: 12px !important;
            right: 12px !important;
            max-width: none !important;
            width: auto !important;
            margin-left: 0 !important;
          }
        }

        /* Tablet and desktop - anchored to right */
        @media (min-width: 481px) {
          .friends-dropdown-panel {
            left: auto !important;
            right: 0 !important;
            width: 320px !important;
            position: absolute !important;
            top: calc(100% + 8px) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default FriendsOnlineWidget;
